import React from "react";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";

// The Story Studio's jobs (GET /api/v2/admin/generations): every picture, clip,
// film, voice, sheet, brief and publish the v2 engine ran, from the server's
// `studio_jobs` table. A row is written when the job starts and again when it
// ends, so polling shows what is running now as well as what failed.
const REFRESH_INTERVAL_MS = 5000;

// Mirrors the server's STUDIO_JOB_STATUSES (studio-jobs.repository.ts).
// "interrupted": the engine process stopped with the job in flight.
const STATUSES = ["running", "done", "error", "interrupted"] as const;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatElapsed = (ms?: number | null) => {
  if (ms == null || Number.isNaN(ms)) {
    return "-";
  }

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes <= 0) {
    return `${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
};

const statusColor = (status: string): "default" | "info" | "success" | "warning" | "error" => {
  switch (status) {
    case "running":
      return "info";
    case "done":
      return "success";
    case "error":
      return "error";
    case "interrupted":
      return "warning";
    default:
      return "default";
  }
};

export const StoryGenerationList = () => {
  const { dataGridProps, filters, setFilters } = useDataGrid({
    queryOptions: {
      refetchInterval: REFRESH_INTERVAL_MS,
    },
  });
  const statusFilter = filters.find((f) => "field" in f && f.field === "status")?.value ?? "";

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        minWidth: 80,
        display: "flex",
      },
      {
        field: "kind",
        headerName: "Kind",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Chip size="small" variant="outlined" label={row.kind} />,
      },
      {
        field: "label",
        headerName: "For",
        flex: 1,
        minWidth: 200,
        sortable: false,
        display: "flex",
        // What the job is for, in the engine's words. The prompt, when the job
        // keeps one, is in the tooltip.
        renderCell: ({ row }) => (
          <span title={row.prompt ?? row.label ?? undefined}>{row.label ?? "-"}</span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row.stale ? `${row.status} (stale)` : row.status}
            color={statusColor(row.status)}
            title={row.stale ?? undefined}
          />
        ),
      },
      {
        field: "userId",
        headerName: "User",
        minWidth: 140,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (row.userName ? `${row.userId} (${row.userName})` : row.userId),
      },
      {
        field: "title",
        headerName: "Story",
        flex: 1,
        minWidth: 180,
        sortable: false,
        display: "flex",
        // A library job (a character sheet, a style, a voice) has no story.
        renderCell: ({ row }) =>
          row.storyId ? (
            <span title={row.storyId}>{row.title ?? <code>{row.storyId}</code>}</span>
          ) : (
            <span>library</span>
          ),
      },
      {
        field: "elapsedMs",
        headerName: "Elapsed",
        minWidth: 100,
        sortable: false,
        display: "flex",
        valueFormatter: (value) => formatElapsed(value as number | null | undefined),
      },
      {
        field: "steps",
        headerName: "Steps",
        minWidth: 80,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.steps ? (
            <span title={(row.stepMs ?? []).map((ms: number | null) => formatElapsed(ms)).join(" · ")}>
              {row.steps}
            </span>
          ) : (
            <span>-</span>
          ),
      },
      {
        field: "startedAt",
        headerName: "Started",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "finishedAt",
        headerName: "Finished",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "error",
        headerName: "Error",
        flex: 1,
        minWidth: 200,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (row.error ? <span title={row.error}>{row.error}</span> : <span>-</span>),
      },
      {
        field: "jobId",
        headerName: "Job ID",
        minWidth: 180,
        sortable: false,
        display: "flex",
        // The engine's job id, as the server's `[jobs]` log lines carry it.
        renderCell: ({ row }) => <code title={row.jobId}>{row.jobId}</code>,
      },
    ],
    []
  );

  return (
    <List canCreate={false}>
      <TextField
        select
        size="small"
        label="Status"
        value={statusFilter}
        onChange={(e) =>
          setFilters(e.target.value ? [{ field: "status", operator: "eq", value: e.target.value }] : [], "replace")
        }
        sx={{ mb: 2, minWidth: 180 }}
      >
        <MenuItem value="">All statuses</MenuItem>
        {STATUSES.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </TextField>
      <DataGrid {...dataGridProps} columns={columns} />
    </List>
  );
};
